import {
    faCircleNotch,
    faSave,
    faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React from 'react';
import {
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    FormGroup,
    Input,
    Label,
    Modal,
    Table,
} from 'reactstrap';
import { ApiRoutes } from '../ApiRoutes';
import { Can } from '../Can';
import { util } from '../Util';
import { toasty } from '../common/forms/FormElements';

export default class TimesheetExportSearchButton extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            showModal: false,
            dateFrom: moment().subtract(5, 'weeks').format('YYYY-MM-DD'),
            dateTo: moment().format('YYYY-MM-DD'),
            isSearching: false,
            hasSearched: false,
            exportIds: [],
        };
    }

    openModal = async () => {
        this.setState({ showModal: true }, () => this.searchExports());
    };

    closeModal = () => this.setState({ showModal: false, exportIds: [] });

    isDateRangeValid = () => {
        const { dateFrom, dateTo } = this.state;
        const isFromDateValid = moment(dateFrom, 'YYYY-MM-DD', true).isValid();
        const isToDateValid = moment(dateTo, 'YYYY-MM-DD', true).isValid();

        return (
            isFromDateValid &&
            isToDateValid &&
            moment(dateFrom).isSameOrBefore(moment(dateTo), 'day')
        );
    };

    searchExports = async () => {
        if (!this.isDateRangeValid()) {
            return;
        }

        const { dateFrom, dateTo } = this.state;
        this.setState({ isSearching: true, hasSearched: false });

        try {
            const response = await util.fetch.post(
                ApiRoutes.contractTimesheetManagement.searchExportIds(),
                { dateFrom, dateTo }
            );

            this.setState({ exportIds: response ?? [] });
        } catch {
            this.setState({ exportIds: [] });
            toasty.error('Error searching timesheet exports');
        } finally {
            this.setState({
                isSearching: false,
                hasSearched:
                    this.state.dateFrom === dateFrom &&
                    this.state.dateTo === dateTo,
            });
        }
    };

    downloadExport = async (id) => {
        const { onDownloadExport } = this.props;
        await onDownloadExport(id);
    };

    render() {
        const {
            showModal,
            dateFrom,
            dateTo,
            isSearching,
            hasSearched,
            exportIds,
        } = this.state;

        const isSearchDisabled = isSearching || !this.isDateRangeValid();

        return (
            <Can I="export" a="timesheet">
                <span className="timesheet-management-action-button-wrap">
                    <Button
                        size="sm"
                        className="timesheet-management-action-button"
                        color="success"
                        onClick={this.openModal}
                    >
                        <FontAwesomeIcon icon={faSearch} className="mr-2" />
                        Find Exports
                    </Button>
                </span>

                <Modal isOpen={showModal} toggle={this.closeModal} size="lg">
                    <Card className="mb-0" toggle={this.closeModal}>
                        <CardHeader className="h3" toggle={this.closeModal}>
                            Find Timesheet Exports
                        </CardHeader>
                        <CardBody>
                            <div className="d-flex align-items-end flex-wrap">
                                <FormGroup className="mr-3">
                                    <Label for="exportFromDate">From</Label>
                                    <Input
                                        id="exportFromDate"
                                        type="date"
                                        value={dateFrom}
                                        invalid={
                                            Boolean(dateFrom) &&
                                            !moment(
                                                dateFrom,
                                                'YYYY-MM-DD',
                                                true
                                            ).isValid()
                                        }
                                        onChange={(event) =>
                                            this.setState({
                                                dateFrom: event.target.value,
                                                hasSearched: false,
                                            })
                                        }
                                    />
                                </FormGroup>
                                <FormGroup className="mr-3">
                                    <Label for="exportToDate">To</Label>
                                    <Input
                                        id="exportToDate"
                                        type="date"
                                        value={dateTo}
                                        invalid={
                                            Boolean(dateTo) &&
                                            !moment(
                                                dateTo,
                                                'YYYY-MM-DD',
                                                true
                                            ).isValid()
                                        }
                                        onChange={(event) =>
                                            this.setState({
                                                dateTo: event.target.value,
                                                hasSearched: false,
                                            })
                                        }
                                    />
                                </FormGroup>
                            </div>
                            <Button
                                color="success"
                                outline={hasSearched}
                                className="mb-3"
                                disabled={isSearchDisabled}
                                onClick={this.searchExports}
                            >
                                <FontAwesomeIcon
                                    icon={
                                        isSearching ? faCircleNotch : faSearch
                                    }
                                    className={
                                        isSearching ? 'fa-spin mr-2' : 'mr-2'
                                    }
                                />
                                Search
                            </Button>
                            <div className="table-responsive">
                                <Table striped hover>
                                    <thead>
                                        <tr>
                                            <th>Timesheet Export</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {exportIds.map((eid) => (
                                            <tr>
                                                <td>
                                                    <Button
                                                        type="button"
                                                        onClick={() =>
                                                            this.downloadExport(
                                                                eid
                                                            )
                                                        }
                                                        color="success"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faSave}
                                                            className="mr-1"
                                                        />
                                                        Download Export #{eid}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                        <CardFooter>
                            <Button
                                color="primary"
                                block
                                onClick={this.closeModal}
                            >
                                Close
                            </Button>
                        </CardFooter>
                    </Card>
                </Modal>
            </Can>
        );
    }
}
